import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, ExternalLink, Play, Calendar, Eye, Heart, MessageCircle, Share2, ArrowLeft, Video, ChevronDown, ChevronUp, X, ArrowUpDown, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import VideoPreviewModal from '../components/VideoPreviewModal';
import { useTheme } from '../contexts/ThemeContext';

const VideoInspirationLibrary = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedVideos, setGroupedVideos] = useState({});
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [videoPreview, setVideoPreview] = useState(null); // { videoUrl, creator, platform, views, likes, video }
  const [sortBy, setSortBy] = useState('posted_date_desc'); // Sort option
  const [searchQuery, setSearchQuery] = useState(''); // Search query for metadata

  useEffect(() => {
    if (user?.email) {
      loadVideos();
    }
  }, [user]);

  useEffect(() => {
    // Calculate date range from videos
    if (videos.length > 0) {
      const dates = videos
        .map(v => {
          const dateStr = v.apify_posted_date || v.created_at;
          if (!dateStr) return null;
          const date = new Date(dateStr);
          return isNaN(date.getTime()) ? null : date;
        })
        .filter(d => d !== null)
        .sort((a, b) => a - b);
      
      if (dates.length > 0) {
        setDateRange({
          start: dates[0],
          end: dates[dates.length - 1]
        });
      } else {
        setDateRange({ start: null, end: null });
      }
    } else {
      setDateRange({ start: null, end: null });
    }
  }, [videos]);

  // Filter videos based on search query (partial match across all metadata)
  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) {
      return videos;
    }

    const query = searchQuery.toLowerCase().trim();
    
    return videos.filter(video => {
      // Search in visual description
      if (video.visual_description && video.visual_description.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in audio description
      if (video.audio_description && video.audio_description.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in text overlay description
      if (video.text_overlay_description && video.text_overlay_description.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in most distinct thing
      if (video.most_distinct_thing && video.most_distinct_thing.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in creator username
      if (video.apify_author_username && video.apify_author_username.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in caption
      if (video.apify_caption && video.apify_caption.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in trend names
      if (video.trends && video.trends.length > 0) {
        const trendMatch = video.trends.some(trend => 
          trend.trend_name && trend.trend_name.toLowerCase().includes(query)
        );
        if (trendMatch) return true;
      }
      
      // Search in tags
      if (video.user_tags && video.user_tags.length > 0) {
        const tagMatch = video.user_tags.some(tag => 
          tag && tag.toLowerCase().includes(query)
        );
        if (tagMatch) return true;
      }
      
      // Search in hashtags
      if (video.apify_hashtags && video.apify_hashtags.length > 0) {
        const hashtagMatch = video.apify_hashtags.some(hashtag => 
          hashtag && hashtag.toLowerCase().includes(query)
        );
        if (hashtagMatch) return true;
      }
      
      // Search in music name/author
      if (video.apify_music_name && video.apify_music_name.toLowerCase().includes(query)) {
        return true;
      }
      if (video.apify_music_author && video.apify_music_author.toLowerCase().includes(query)) {
        return true;
      }
      
      return false;
    });
  }, [videos, searchQuery]);

  // Sort videos based on selected sort option
  const sortedVideos = useMemo(() => {
    const sorted = [...filteredVideos];
    
    switch (sortBy) {
      case 'posted_date_desc':
        sorted.sort((a, b) => {
          const dateA = new Date(a.apify_posted_date || a.created_at || 0);
          const dateB = new Date(b.apify_posted_date || b.created_at || 0);
          return dateB - dateA; // Newest first
        });
        break;
      case 'posted_date_asc':
        sorted.sort((a, b) => {
          const dateA = new Date(a.apify_posted_date || a.created_at || 0);
          const dateB = new Date(b.apify_posted_date || b.created_at || 0);
          return dateA - dateB; // Oldest first
        });
        break;
      case 'views_desc':
        sorted.sort((a, b) => {
          const viewsA = a.apify_views || 0;
          const viewsB = b.apify_views || 0;
          return viewsB - viewsA; // Highest first
        });
        break;
      case 'views_asc':
        sorted.sort((a, b) => {
          const viewsA = a.apify_views || 0;
          const viewsB = b.apify_views || 0;
          return viewsA - viewsB; // Lowest first
        });
        break;
      case 'likes_desc':
        sorted.sort((a, b) => {
          const likesA = a.apify_likes || 0;
          const likesB = b.apify_likes || 0;
          return likesB - likesA; // Highest first
        });
        break;
      case 'likes_asc':
        sorted.sort((a, b) => {
          const likesA = a.apify_likes || 0;
          const likesB = b.apify_likes || 0;
          return likesA - likesB; // Lowest first
        });
        break;
      case 'creator_asc':
        sorted.sort((a, b) => {
          const creatorA = (a.apify_author_username || '').toLowerCase();
          const creatorB = (b.apify_author_username || '').toLowerCase();
          return creatorA.localeCompare(creatorB); // A-Z
        });
        break;
      case 'creator_desc':
        sorted.sort((a, b) => {
          const creatorA = (a.apify_author_username || '').toLowerCase();
          const creatorB = (b.apify_author_username || '').toLowerCase();
          return creatorB.localeCompare(creatorA); // Z-A
        });
        break;
      default:
        // Default: newest first
        sorted.sort((a, b) => {
          const dateA = new Date(a.apify_posted_date || a.created_at || 0);
          const dateB = new Date(b.apify_posted_date || b.created_at || 0);
          return dateB - dateA;
        });
    }
    
    return sorted;
  }, [filteredVideos, sortBy]);

  useEffect(() => {
    // Group videos by trend or create categories
    const grouped = {};
    
    sortedVideos.forEach(video => {
      // Group by associated trends if available
      if (video.trends && video.trends.length > 0) {
        video.trends.forEach(trend => {
          const key = trend.trend_name || 'Uncategorized';
          if (!grouped[key]) {
            grouped[key] = {
              name: key,
              description: `Videos associated with the "${key}" trend`,
              videos: []
            };
          }
          grouped[key].videos.push(video);
        });
      } else {
        // Videos without trends go to "General Inspiration"
        const key = 'General Inspiration';
        if (!grouped[key]) {
          grouped[key] = {
            name: key,
            description: 'Videos with diverse creative approaches and hooks',
            videos: []
          };
        }
        grouped[key].videos.push(video);
      }
    });

    setGroupedVideos(grouped);
  }, [sortedVideos]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/trend-spotter/library?userId=${encodeURIComponent(user.email)}&limit=1000`);
      const data = await response.json();
      
      if (data.success) {
        setVideos(data.videos || []);
      } else {
        toast.error('Failed to load videos');
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Unknown date';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return 'Unknown date';
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Highlight timestamps and phrases before colons in text
  const highlightTimestamps = (text) => {
    if (!text) return text;
    
    // Process text to highlight both timestamps and phrases before colons
    // Split by potential matches and rebuild with highlights
    let processedText = text;
    const parts = [];
    let keyCounter = 0;
    let lastIndex = 0;

    // Combined pattern: timestamps OR phrases before colons
    // Timestamps: **0:00-0:02:** or 0:00-0:02 (with optional ** markers)
    // Phrases: **Main Subject(s):** or * **Main Subject(s):** (text in bold before colon)
    const pattern = /(\*\*)?(\d+:\d+-\d+:\d+)(:\*\*)?|(\*?\s*)?\*\*([^*:]+?):\*\*/g;
    
    let match;
    while ((match = pattern.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      // Check if it's a timestamp (has the digit pattern)
      if (match[2] && /^\d+:\d+-\d+:\d+$/.test(match[2])) {
        // Highlight timestamp
        parts.push(
          <span key={`ts-${keyCounter++}`} className="font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
            {match[2]}
          </span>
        );
      } else if (match[5]) {
        // It's a phrase before colon (match[5] is the text inside **...:**)
        const phrase = match[5].trim();
        // Only highlight if it looks like a section header (reasonable length, not too long)
        if (phrase.length > 0 && phrase.length < 50) {
          parts.push(
            <span key={`phrase-${keyCounter++}`} className="font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
              {phrase}:
            </span>
          );
        } else {
          // Not a phrase to highlight, add original text
          parts.push(match[0]);
        }
      } else {
        // Fallback: add original match
        parts.push(match[0]);
      }

      lastIndex = pattern.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const getVideoUrl = (video) => {
    // Try video_url column first
    if (video.video_url) return video.video_url;
    
    // Try apify_metadata
    if (video.apify_metadata) {
      try {
        const meta = typeof video.apify_metadata === 'string' 
          ? JSON.parse(video.apify_metadata) 
          : video.apify_metadata;
        return meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl || null;
      } catch (e) {
        console.error('Error parsing apify_metadata:', e);
      }
    }
    
    return null;
  };

  const getPlatform = (video) => {
    if (video.platform) return video.platform.toLowerCase();
    if (video.source_url) {
      if (video.source_url.includes('tiktok.com')) return 'tiktok';
      if (video.source_url.includes('instagram.com')) return 'instagram';
    }
    return 'unknown';
  };

  const getVerbalHook = (video) => {
    // Priority: audio_description > text_overlay_description > caption
    // These are AI-generated fields from the database columns:
    // - audio_description (TEXT) - AI analysis of audio/voiceover
    // - text_overlay_description (TEXT) - AI analysis of text overlays
    // - apify_caption (TEXT) - Original caption from platform
    if (video.audio_description && video.audio_description.trim()) {
      return video.audio_description;
    }
    if (video.text_overlay_description && video.text_overlay_description.trim()) {
      return video.text_overlay_description;
    }
    if (video.apify_caption && video.apify_caption.trim()) {
      // Extract first sentence or first 150 chars
      const caption = video.apify_caption.trim();
      const firstSentence = caption.split(/[.!?]/)[0].trim();
      if (firstSentence && firstSentence.length > 0) {
        return firstSentence.length > 150 ? firstSentence.substring(0, 150) + '...' : firstSentence;
      }
      // If no sentence break, return first 150 chars
      return caption.length > 150 ? caption.substring(0, 150) + '...' : caption;
    }
    return null;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={isDark ? 'text-zinc-300' : 'text-gray-600'}>Loading inspiration library...</p>
        </div>
      </div>
    );
  }

  const dateRangeStr = dateRange.start && dateRange.end
    ? `${dateRange.start.toLocaleDateString('en-US', { month: 'short' })} - ${dateRange.end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
    : '';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} border-b sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/trend-spotter')}
                className={`p-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
                title="Back to Trend Spotter"
              >
                <ArrowLeft className={`h-5 w-5 ${isDark ? 'text-zinc-300' : 'text-gray-600'}`} />
              </button>
              <div className="flex items-center gap-2">
                <Sparkles className={`h-6 w-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Video Inspiration Library</h1>
              </div>
              {dateRangeStr && (
                <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>{dateRangeStr}</span>
              )}
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              {/* Search Input */}
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-zinc-400' : 'text-gray-400'}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search metadata (visual, audio, text, trends, tags...)"
                  className={`w-full pl-10 pr-4 py-2 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-zinc-400 hover:text-zinc-200' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className={`h-4 w-4 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`} />
                <label htmlFor="sort-select" className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>Sort by:</label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={`px-3 py-1.5 ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                >
                  <option value="posted_date_desc">Posted Date (Newest)</option>
                  <option value="posted_date_asc">Posted Date (Oldest)</option>
                  <option value="views_desc">Views (Highest)</option>
                  <option value="views_asc">Views (Lowest)</option>
                  <option value="likes_desc">Likes (Highest)</option>
                  <option value="likes_asc">Likes (Lowest)</option>
                  <option value="creator_asc">Creator (A-Z)</option>
                  <option value="creator_desc">Creator (Z-A)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {Object.keys(groupedVideos).length === 0 ? (
          <div className="text-center py-16">
            <p className={`${isDark ? 'text-zinc-400' : 'text-gray-500'} text-lg`}>No videos found in your library</p>
            <button
              onClick={() => navigate('/trend-spotter?view=library')}
              className={`mt-4 ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'} font-medium`}
            >
              Add videos to get started →
            </button>
          </div>
        ) : (
          Object.entries(groupedVideos).map(([key, group]) => (
            <div key={key} className="mb-12">
              {/* Category Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{group.name}</h2>
                </div>
                <p className={`${isDark ? 'text-zinc-400' : 'text-gray-600'} text-sm ml-4 max-w-3xl`}>{group.description}</p>
              </div>

              {/* Video Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {group.videos.map((video) => {
                  const videoUrl = getVideoUrl(video);
                  const platform = getPlatform(video);
                  const verbalHook = getVerbalHook(video);
                  const isHovered = hoveredVideo === video.id;

                  return (
                    <div
                      key={video.id}
                      className={`${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden hover:shadow-lg transition-shadow`}
                      onMouseEnter={() => setHoveredVideo(video.id)}
                      onMouseLeave={() => setHoveredVideo(null)}
                    >
                      <div className="flex gap-4 p-4">
                        {/* Video Thumbnail - Clickable */}
                        <div className="relative flex-shrink-0">
                          <div 
                            className={`w-24 h-40 ${isDark ? 'bg-zinc-800' : 'bg-gray-200'} rounded-lg overflow-hidden relative cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all`}
                            onClick={() => {
                              if (videoUrl) {
                                setVideoPreview({
                                  videoUrl,
                                  creator: video.apify_author_username || 'unknown',
                                  platform: platform,
                                  views: video.apify_views || 0,
                                  likes: video.apify_likes || 0,
                                  video: video
                                });
                              }
                            }}
                          >
                            {videoUrl ? (
                              <>
                                <video
                                  src={videoUrl}
                                  className="w-full h-full object-cover"
                                  playsInline
                                  loop
                                  onMouseEnter={(e) => {
                                    e.target.play().catch(() => {});
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.pause();
                                    e.target.currentTime = 0;
                                  }}
                                />
                                {!isHovered && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                                    <Play className="h-6 w-6 text-white opacity-70" />
                                  </div>
                                )}
                                <div className="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded">
                                  1.0x
                                </div>
                              </>
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                                <Video className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Video Info */}
                        <div className="flex-1 min-w-0">
                          {/* Creator & Platform */}
                          <div className="mb-2">
                            <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              @{video.apify_author_username || 'unknown'}
                            </div>
                            <div className={`flex items-center gap-3 text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'} mt-1 flex-wrap`}>
                              <span className="capitalize">{platform}</span>
                              <span>•</span>
                              <span>{formatDate(video.apify_posted_date || video.created_at)}</span>
                              <span>•</span>
                              <span>{formatNumber(video.apify_views || video.apify_likes || 0)} views</span>
                            </div>
                          </div>

                          {/* Trend Name */}
                          {video.trends && video.trends.length > 0 && (
                            <div className="mb-2">
                              <div className={`text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-gray-500'} uppercase mb-1`}>Trend</div>
                              <div className="flex flex-wrap gap-1">
                                {video.trends.map((trend, idx) => (
                                  <span key={idx} className={`text-xs ${isDark ? 'bg-purple-900/50 text-purple-200' : 'bg-purple-100 text-purple-700'} px-2 py-0.5 rounded`}>
                                    {trend.trend_name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Tags */}
                          {video.user_tags && video.user_tags.length > 0 && (
                            <div className="mb-2">
                              <div className={`text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-gray-500'} uppercase mb-1`}>Tags</div>
                              <div className="flex flex-wrap gap-1">
                                {video.user_tags.map((tag, idx) => (
                                  <span key={idx} className={`text-xs ${isDark ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-700'} px-2 py-0.5 rounded`}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Hashtags */}
                          {video.apify_hashtags && video.apify_hashtags.length > 0 && (
                            <div className="mb-2">
                              <div className={`text-xs font-semibold ${isDark ? 'text-zinc-400' : 'text-gray-500'} uppercase mb-1`}>Hashtags</div>
                              <div className="flex flex-wrap gap-1">
                                {video.apify_hashtags.slice(0, 5).map((hashtag, idx) => (
                                  <span key={idx} className={`text-xs ${isDark ? 'bg-zinc-700 text-zinc-200' : 'bg-gray-100 text-gray-700'} px-2 py-0.5 rounded`}>
                                    #{hashtag.replace(/^#/, '')}
                                  </span>
                                ))}
                                {video.apify_hashtags.length > 5 && (
                                  <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>+{video.apify_hashtags.length - 5} more</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Most Distinct Thing - Always visible by default */}
                          {video.most_distinct_thing && (
                            <div className="mb-2">
                              <div className={`text-sm font-medium ${isDark ? 'text-green-100 bg-green-900/30' : 'text-gray-700 bg-green-50'} px-2 py-1 rounded inline-block`}>
                                {video.most_distinct_thing}
                              </div>
                            </div>
                          )}

                          {/* Action Links */}
                          <div className={`flex items-center gap-4 mt-3 pt-3 border-t ${isDark ? 'border-zinc-700' : 'border-gray-100'}`}>
                            <a
                              href={video.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-sm ${isDark ? 'text-zinc-300 hover:text-white' : 'text-gray-600 hover:text-gray-700'} font-medium flex items-center gap-1`}
                            >
                              Original <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Video Preview Modal - Wide Window with 3 Sections */}
      {videoPreview && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="relative w-full max-w-7xl bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {videoPreview.platform === 'tiktok' ? (
                  <div className="w-6 h-6 bg-black rounded flex items-center justify-center text-[10px] text-white">TT</div>
                ) : (
                  <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center text-[10px] text-white">IG</div>
                )}
                <div>
                  <div className="font-semibold text-gray-900">@{videoPreview.creator}</div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {videoPreview.views > 0 && (
                      <span>{formatNumber(videoPreview.views)} views</span>
                    )}
                    {videoPreview.likes > 0 && (
                      <span>{formatNumber(videoPreview.likes)} likes</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setVideoPreview(null)}
                className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
            </div>

            {/* Main Content - 4 Columns: Video Preview, Visual, Audio, Text Overlay */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Column 1: Video Preview - 9:16 Aspect Ratio */}
                <div className="bg-black rounded-lg overflow-hidden w-full">
                  <div className="w-full aspect-[9/16] flex items-center justify-center">
                    <video
                      src={videoPreview.videoUrl}
                      className="w-full h-full object-contain"
                      controls
                      autoPlay
                      playsInline
                      muted={false}
                      preload="metadata"
                      onError={(e) => {
                        console.error('Video failed to load:', videoPreview.videoUrl);
                        toast.error('Failed to load video');
                      }}
                    />
                  </div>
                </div>

                {/* Column 2: Visual - Match video height and scrollable */}
                <div className="bg-blue-50 rounded-lg border border-blue-200 flex flex-col w-full aspect-[9/16]">
                  <div className="flex items-center gap-2 mb-3 p-4 pb-2 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <h3 className="text-base font-bold text-gray-900">Visual</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {videoPreview.video?.visual_description ? (
                      <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                        {videoPreview.video.visual_description.split(/[.!?]+/).filter(s => s.trim().length > 0).map((sentence, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                            <span>{highlightTimestamps(sentence.trim())}{idx < videoPreview.video.visual_description.split(/[.!?]+/).filter(s => s.trim().length > 0).length - 1 && '.'}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No visual description available</p>
                    )}
                  </div>
                </div>

                {/* Column 3: Audio - Match video height and scrollable */}
                <div className="bg-purple-50 rounded-lg border border-purple-200 flex flex-col w-full aspect-[9/16]">
                  <div className="flex items-center gap-2 mb-3 p-4 pb-2 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <h3 className="text-base font-bold text-gray-900">Audio</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {videoPreview.video?.audio_description ? (
                      <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                        {videoPreview.video.audio_description.split(/[.!?]+/).filter(s => s.trim().length > 0).map((sentence, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                            <span>{highlightTimestamps(sentence.trim())}{idx < videoPreview.video.audio_description.split(/[.!?]+/).filter(s => s.trim().length > 0).length - 1 && '.'}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No audio description available</p>
                    )}
                    {/* Music Info */}
                    {videoPreview.video?.apify_music_name && (
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Music</div>
                        <div className="text-sm text-gray-700">
                          {videoPreview.video.apify_music_name}
                          {videoPreview.video.apify_music_author && (
                            <span className="text-gray-500"> by {videoPreview.video.apify_music_author}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Column 4: Text Overlay - Match video height and scrollable */}
                <div className="bg-green-50 rounded-lg border border-green-200 flex flex-col w-full aspect-[9/16]">
                  <div className="flex items-center gap-2 mb-3 p-4 pb-2 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <h3 className="text-base font-bold text-gray-900">Text Overlay</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {videoPreview.video?.text_overlay_description ? (
                      <div className="text-sm text-gray-700 leading-relaxed space-y-2">
                        {videoPreview.video.text_overlay_description.split(/[.!?]+/).filter(s => s.trim().length > 0).map((sentence, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                            <span>{highlightTimestamps(sentence.trim())}{idx < videoPreview.video.text_overlay_description.split(/[.!?]+/).filter(s => s.trim().length > 0).length - 1 && '.'}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No text overlay description available</p>
                    )}
                    {/* Original Caption */}
                    {videoPreview.video?.apify_caption && (
                      <div className="mt-4 pt-4 border-t border-green-200">
                        <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Original Caption</div>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {videoPreview.video.apify_caption}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Metadata - Trend, Tags, Hashtags */}
              {videoPreview.video && (
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                  {/* Trend */}
                  {videoPreview.video.trends && videoPreview.video.trends.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Trend</div>
                      <div className="flex flex-wrap gap-2">
                        {videoPreview.video.trends.map((trend, idx) => (
                          <span key={idx} className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                            {trend.trend_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Tags */}
                  {videoPreview.video.user_tags && videoPreview.video.user_tags.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Tags</div>
                      <div className="flex flex-wrap gap-2">
                        {videoPreview.video.user_tags.map((tag, idx) => (
                          <span key={idx} className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Hashtags */}
                  {videoPreview.video.apify_hashtags && videoPreview.video.apify_hashtags.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Hashtags</div>
                      <div className="flex flex-wrap gap-2">
                        {videoPreview.video.apify_hashtags.map((hashtag, idx) => (
                          <span key={idx} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            #{hashtag.replace(/^#/, '')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoInspirationLibrary;
